import { captureException } from '@sentry/react-router';
import { useEffect } from 'react';
import {
  data,
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';

import { HoneypotProvider } from '@veraclins-dev/form/form';
import { createHoneypot } from '@veraclins-dev/form/server';
import { initImageConfig } from '@veraclins-dev/image';
import { EpicToaster, useToast } from '@veraclins-dev/react-utils';
import { combineHeaders, getToast } from '@veraclins-dev/react-utils/server';
import { Box } from '@veraclins-dev/ui/box';
import { IconProvider } from '@veraclins-dev/ui/icon-provider';
import href from '@veraclins-dev/ui/sprite.svg';
import { Typography } from '@veraclins-dev/ui/typography';
import { cn } from '@veraclins-dev/utils';

import { type Route } from './+types/root';
import { ClientHintCheck } from './components/client-hint-check';
import { Link } from './components/link';
import { enforceDocsAuth } from './utils/auth/session.server';
import { getHints } from './utils/client-hints';
import { type Theme, useTheme } from './utils/theme';
import { getTheme } from './utils/theme/theme.server';
import styles from './styles.css?url';

const honeypot = createHoneypot();

export const meta: Route.MetaFunction = () => {
  const siteName = 'docs.veraclins.com';
  const defaultTitle = 'Veraclins Docs';
  const defaultDescription =
    'Internal documentation for Veraclins packages, products, and engineering references.';

  return [
    { title: defaultTitle },
    { name: 'description', content: defaultDescription },
    { name: 'author', content: 'Clinton Agada' },
    { name: 'theme-color', content: '#000000' },
    // Open Graph
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: siteName },
    { property: 'og:title', content: defaultTitle },
    { property: 'og:description', content: defaultDescription },
    { property: 'og:url', content: 'https://docs.veraclins.com' },
    // Twitter Card
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: defaultTitle },
    { name: 'twitter:description', content: defaultDescription },
    { name: 'twitter:creator', content: '@icstps' },
    // Additional SEO
    { name: 'robots', content: 'index, follow' },
    { name: 'googlebot', content: 'index, follow' },
  ];
};

export const links: Route.LinksFunction = () => [
  { rel: 'stylesheet', href: styles },
  { rel: 'preload', href, as: 'image' },
  {
    rel: 'preload',
    href: '/fonts/poppins-400.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous',
  },
];

export async function loader({ request }: Route.LoaderArgs) {
  // Everything on docs.veraclins.dev is private. enforceDocsAuth throws a
  // redirect to /login for an anonymous visitor on a gated path, so the body
  // below never runs for them.
  await enforceDocsAuth(request);

  const imageConfig = initImageConfig({
    path: '/common/image',
  });
  const honeyProps = await honeypot.getInputProps();
  const { toast, headers: toastHeaders } = await getToast(request);

  return data(
    {
      theme: getTheme(request),
      imageConfig,
      honeyProps,
      toast,
      requestInfo: {
        hints: getHints(request),
        userPrefs: { theme: getTheme(request) },
      },
    },
    {
      headers: combineHeaders(toastHeaders),
    },
  );
}

function Document({
  children,
  theme = 'light',
  imageConfig,
}: {
  children: React.ReactNode;
  theme?: Theme | null;
  imageConfig?: ReturnType<typeof initImageConfig>;
}) {
  const resolvedTheme = theme ?? 'light';
  return (
    <html
      lang="en"
      className={cn(resolvedTheme, 'h-screen w-screen')}
      suppressHydrationWarning
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <ClientHintCheck />
        {imageConfig && (
          <script
            // skipcq: JS-0440
            dangerouslySetInnerHTML={{
              __html: `window.imageConfig = ${JSON.stringify(imageConfig)};`,
            }}
          />
        )}
      </head>
      <body className="bg-background h-full text-sm text-foreground antialiased flex flex-col">
        {/* Skip to main content link for accessibility */}
        {/* oxlint-disable-next-line veraclins-ui/no-bare-html -- hash skip-link; the docs app Link is router-only (to=) and has no href prop */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:shadow-lg"
        >
          Skip to main content
        </a>
        <IconProvider sprite={href}>{children}</IconProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function ErrorBoundary({ error, loaderData }: Route.ErrorBoundaryProps) {
  const theme = loaderData?.theme || null;
  const imageConfig = loaderData?.imageConfig;

  // Capture non-route errors to Sentry
  useEffect(() => {
    if (!isRouteErrorResponse(error) && process.env.NODE_ENV === 'production') {
      captureException(error);
    }
  }, [error]);

  return (
    <Document theme={theme} imageConfig={imageConfig}>
      <Box
        component="main"
        flex="1"
        className="container"
        display="flex"
        flexDirection="column"
        items="center"
        justify="center"
        gap={6}
        paddingY={16}
        paddingX={4}
      >
        {isRouteErrorResponse(error) ? (
          <>
            <Typography variant="h1" className="text-center">
              {error.status} {error.statusText}
            </Typography>
            <Typography
              variant="body1"
              maxW="md"
              className="text-center text-foreground/80"
            >
              {typeof error.data === 'string'
                ? error.data
                : 'An error occurred'}
            </Typography>
          </>
        ) : error instanceof Error ? (
          <>
            <Typography variant="h1" className="text-center">
              Something went wrong
            </Typography>
            <Typography
              variant="body1"
              maxW="md"
              className="text-center text-foreground/80"
            >
              An unexpected error occurred. Please try refreshing the page or
              contact support if the problem persists.
            </Typography>
            {error.message && (
              <Box
                border={1}
                rounded="lg"
                padding={4}
                maxWidth="2xl"
                className="bg-danger/10 border-danger/20"
              >
                <Typography className="text-danger font-mono text-xs">
                  {error.message}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="h1" className="text-center">
            Unknown Error
          </Typography>
        )}
        <Box display="flex" gap={4} flexWrap="wrap" justify="center">
          <Link to="/" type="button" emphasis="filled" tone="brand">
            Go Home
          </Link>
        </Box>
      </Box>
    </Document>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  const theme = useTheme();
  const imageConfig = loaderData.imageConfig;

  useToast(loaderData.toast);

  return (
    <HoneypotProvider {...loaderData.honeyProps}>
      <Document theme={theme} imageConfig={imageConfig}>
        <Box component="main" id="main-content" flex="1" minH={0}>
          <Outlet />
        </Box>
        <EpicToaster position="bottom-right" theme={theme} richColors />
      </Document>
    </HoneypotProvider>
  );
}
